import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Film, Building2, User as UserIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import MarketingNav from "@/components/MarketingNav";
import MarketingFooter from "@/components/MarketingFooter";
import { useAuth } from "@/lib/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

export default function Register() {
  const { register } = useAuth();
  const { t } = useTranslation();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [role, setRole] = useState(params.get("role") === "sponsor" ? "sponsor" : "creator");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await register({ name, email, password, role });
    setLoading(false);
    if (res.ok) {
      toast.success(t("auth.account_created"));
      nav("/dashboard", { replace: true });
    } else {
      toast.error(res.error || t("auth.register_failed"));
    }
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="marketing-shell min-h-screen">
      <MarketingNav />
      <section className="mx-auto max-w-lg px-6 py-16">
        <div className="glass-card rounded-3xl p-10">
          <p className="eyebrow text-gold">{t("nav.register")}</p>
          <h1 className="font-display text-4xl text-white mt-3">{t("auth.register_title")}</h1>
          <p className="text-white/70 mt-3">{t("auth.register_subtitle")}</p>

          <div className="mt-6 grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => setRole("creator")}
              data-testid="button-role-creator"
              className={`rounded-2xl border p-5 text-left transition-colors ${role === "creator" ? "border-gold bg-gold/10" : "border-white/10 bg-white/5 hover:border-white/25"}`}
            >
              <UserIcon size={20} className="text-gold" />
              <p className="mt-3 text-white font-semibold">{t("auth.role_creator")}</p>
              <p className="text-xs text-white/60 mt-1">{t("auth.role_creator_hint")}</p>
            </button>
            <button
              type="button"
              onClick={() => setRole("sponsor")}
              data-testid="button-role-sponsor"
              className={`rounded-2xl border p-5 text-left transition-colors ${role === "sponsor" ? "border-gold bg-gold/10" : "border-white/10 bg-white/5 hover:border-white/25"}`}
            >
              <Building2 size={20} className="text-gold" />
              <p className="mt-3 text-white font-semibold">{t("auth.role_sponsor")}</p>
              <p className="text-xs text-white/60 mt-1">{t("auth.role_sponsor_hint")}</p>
            </button>
          </div>

          <button
            onClick={google}
            type="button"
            className="mt-6 w-full inline-flex items-center justify-center gap-3 rounded-full bg-white text-slate-900 py-3 font-semibold hover:bg-white/90"
            data-testid="button-register-google"
          >
            {t("auth.or_google")}
          </button>
          <div className="my-6 flex items-center gap-3 text-white/40 text-xs">
            <div className="h-px bg-white/10 flex-1" /> {t("auth.email")} <div className="h-px bg-white/10 flex-1" />
          </div>

          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <Label className="text-white/80 text-sm">{t("auth.name")}</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} required autoComplete="name"
                data-testid="input-register-name"
                className="mt-1 bg-white/5 border-white/10 text-white placeholder:text-white/40 focus-visible:ring-gold" />
            </div>
            <div>
              <Label className="text-white/80 text-sm">{t("auth.email")}</Label>
              <Input value={email} onChange={(e) => setEmail(e.target.value)} type="email" required autoComplete="email"
                data-testid="input-register-email"
                className="mt-1 bg-white/5 border-white/10 text-white placeholder:text-white/40 focus-visible:ring-gold" />
            </div>
            <div>
              <Label className="text-white/80 text-sm">{t("auth.password")}</Label>
              <Input value={password} onChange={(e) => setPassword(e.target.value)} type="password" required autoComplete="new-password" minLength={6}
                data-testid="input-register-password"
                className="mt-1 bg-white/5 border-white/10 text-white placeholder:text-white/40 focus-visible:ring-gold" />
              <p className="mt-1 text-xs text-white/40">{t("auth.password_hint")}</p>
            </div>
            <Button type="submit" disabled={loading} data-testid="button-register-submit" className="w-full gold-cta rounded-full py-6 text-base font-semibold">
              {loading ? t("common.loading") : t("auth.submit_register")}
            </Button>
          </form>

          <p className="mt-8 text-sm text-white/70 text-center">
            {t("auth.have_account")}{" "}
            <Link to="/login" className="text-gold hover:underline" data-testid="link-to-login">
              {t("auth.go_to_login")}
            </Link>
          </p>
        </div>
      </section>
      <MarketingFooter />
    </div>
  );
}
